import { Button, Input, Typography } from '@breetometer/components';
import { RewardPointsAvailablePointsIcon, RewardPointsIcon } from 'BreetAssets';
import {
	checkNearestValidAmount,
	DEFAULT_INPUT_MAX_LENGTH,
	getCleanedValue,
	getNumbericValue,
	handleDecimalPoints,
	type IUseAccessCredentialsRequest,
	showToast,
} from 'BreetHelpers';
import { useAccessCredentials, useCurrencyOutput, useFormStage } from 'BreetHooks';
import { selectFiatMeta, selectMeta, useAppSelector, useGetRewardPointsQuery, useRedeemRewardsMutation } from 'BreetRedux';
import { type ChangeEvent, type FormEvent, useCallback, useState } from 'react';

import { DialogAccessCredentials } from '../DialogAccessCredentials';
import ModalParts from '../ModalParts';
import type { REDEEMPOINTSSTAGE } from './DialogRewardPoints.helpers';

export const DialogRewardPoints = () => {
	const metaData = useAppSelector(selectMeta);
	const fiatMeta = useAppSelector(selectFiatMeta);

	const { data: rewardPointsData } = useGetRewardPointsQuery();
	const rewardPoints = rewardPointsData?.data;

	const totalRewardPoints = rewardPoints?.reduce<number>((acc, i) => acc + i.points, 0) ?? 0;

	const [redeemRewards, { isLoading }] = useRedeemRewardsMutation();

	const { navigator, isStageActive } = useFormStage<REDEEMPOINTSSTAGE>('redeem-points', ['redeem-points', 'otp-verification']);

	const [points, setPoints] = useState<string>('0');
	const [error, setError] = useState<string>('');

	const currencyOutput = useCurrencyOutput();

	const maximumRedeemPoints = totalRewardPoints;
	const minimumRedeemPoints = metaData?.settings.rewardMeta.vipRedeemLimit[fiatMeta.currencyLower] ?? 0;

	const handleOnChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			let cleanedValue = getCleanedValue(e.target.value);

			cleanedValue = handleDecimalPoints(cleanedValue);

			setPoints(currencyOutput(cleanedValue) ?? '0');

			const numericAmount = getNumbericValue(cleanedValue);

			if (numericAmount > maximumRedeemPoints) {
				setError(`The maximum points to redeem is ${currencyOutput(maximumRedeemPoints)}.`);
				return;
			}
			if (numericAmount < minimumRedeemPoints) {
				setError(`The minimum points to redeem is ${currencyOutput(minimumRedeemPoints)}.`);
				return;
			}

			const nearestValidAmountError = checkNearestValidAmount(numericAmount, fiatMeta.currency);

			if (nearestValidAmountError) {
				setError(nearestValidAmountError);
				return;
			}

			setError('');
		},
		[currencyOutput, fiatMeta.currency, maximumRedeemPoints, minimumRedeemPoints]
	);

	const redeemPoints = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			if (data?.code) {
				redeemRewards({ pin: data.code, points: String(getNumbericValue(points)) })
					.unwrap()
					.then(() => {
						showToast('success', 'Reward Points redeemed successfully');
						data.onSuccess?.();
					})
					.catch((e: unknown) => data.onError(e));
			}
		},
		[points, redeemRewards]
	);

	const { component, handleVerification } = useAccessCredentials({
		id: 'redeem-points',
		mode: 'pin',
		submitRequest: redeemPoints,
		closeModalId: 'rewardPoints',
		dialogMessage: 'Redeeming Points...',
		type: 'collect',
		render: (props) => <DialogAccessCredentials {...props} />,
	});

	const handleSubmit = useCallback(
		(e: FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			handleVerification();
			navigator.goTo('otp-verification')();
		},
		[navigator, handleVerification]
	);

	return (
		<ModalParts.Wrapper>
			<ModalParts.Stage hide={!isStageActive('redeem-points')}>
				<div className='dialogrewardpoints'>
					<RewardPointsIcon className='dialogrewardpoints_headericon' />

					<div className='dialogrewardpoints_header'>
						<Typography as='h1'>Redeem Points</Typography>
						<Typography as='p'>
							Enter the number of points to redeem. The minimum redeemable point is {minimumRedeemPoints.toLocaleString()}.
						</Typography>
					</div>

					<div className='dialogrewardpoints_availablepoints'>
						<RewardPointsAvailablePointsIcon />
						<Typography as='p'>Available Points - {totalRewardPoints.toLocaleString()}</Typography>
					</div>

					<form onSubmit={handleSubmit}>
						<Input
							label='Enter Amount'
							id=''
							widthType='full'
							required
							placeholder=' '
							maxLength={DEFAULT_INPUT_MAX_LENGTH}
							value={points}
							onChange={handleOnChange}
						/>

						{!!error && (
							<Typography
								as='p'
								className='bt_input-error-message'
							>
								{error}
							</Typography>
						)}

						<Button
							label='Redeem'
							type='submit'
							disabled={!getNumbericValue(points) || !!error}
							isLoading={isLoading}
						/>
					</form>
				</div>
			</ModalParts.Stage>

			<ModalParts.Stage hide={!isStageActive('otp-verification')}>{component}</ModalParts.Stage>
		</ModalParts.Wrapper>
	);
};
